import { useMemo, useState } from 'react';
import { scheduleActions, scheduleSelectors } from 'features/schedule/redux';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import { SUBJECT_COLORS } from 'features/schedule/model';
import type { EducationClass, ScheduleErrorTimeslot, ScheduleLesson } from 'services/api';

const DEFAULT_CARD_STYLE = { backgroundColor: '#fff', color: '#000' };
const SELECTED_CARD_STYLE = {
  color: '#000',
  border: '2px solid #6da5f3',
  zIndex: 2,
};

export const useLessonCard = (lesson: ScheduleLesson) => {
  const dispatch = useAppDispatch();

  const selectedLesson = useAppSelector(scheduleSelectors.getSelectedLesson);
  const coloredLessons = useAppSelector(scheduleSelectors.getColoredLessons);
  const editMode = useAppSelector(scheduleSelectors.getEditMode);

  const [editLessonModalOpen, setEditLessonModalOpen] = useState(false);

  const lessonChange = editMode?.changesByLessonId[lesson.id];

  const isPinChanged = lessonChange?.pinned !== undefined;
  const isTeacherChanged = lessonChange?.teacherId !== undefined;
  const isRoomChanged = lessonChange?.roomId !== undefined;
  const isTimeslotChanged = lessonChange?.timeslot !== undefined;

  const isEditMode = !!editMode;

  const handleTogglePinned = () => {
    dispatch(
      scheduleActions.applyDraftLessonPatch({
        lessonId: lesson.id,
        patch: {
          pinned: !lesson.pinned,
        },
      }),
    );
  };

  const handleOpenEditLessonModal = () => setEditLessonModalOpen(true);

  const handleCloseEditLessonModal = () => setEditLessonModalOpen(false);

  const handleOpenLessonDetails = (
    lessonToOpen: ScheduleLesson,
    slotKey: string,
    errors?: ScheduleErrorTimeslot[],
    educationalClass?: EducationClass,
  ) => {
    dispatch(
      scheduleActions.setSelectedLesson({
        lesson: lessonToOpen,
        slotKey,
        errors,
        educationalClass,
      }),
    );
  };

  const cardStyle = useMemo(() => {
    if (selectedLesson?.lesson?.id === lesson.id) {
      return SELECTED_CARD_STYLE;
    }
    if (coloredLessons) {
      return {
        backgroundColor: SUBJECT_COLORS[lesson.subject.id]?.backgroundColor ?? '#fff',
        color: SUBJECT_COLORS[lesson.subject.id]?.textColor ?? '#000',
      };
    }

    return DEFAULT_CARD_STYLE;
  }, [coloredLessons, lesson.subject.id, selectedLesson?.lesson?.id]);

  return {
    state: {
      cardStyle,
      isPinChanged,
      isTeacherChanged,
      isRoomChanged,
      isTimeslotChanged,
      isEditMode,
      editLessonModalOpen,
    },
    methods: {
      handleOpenLessonDetails,
      handleTogglePinned,
      handleOpenEditLessonModal,
      handleCloseEditLessonModal,
    },
  };
};
